import { useState } from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { DotmCircular4 } from '~/components/ui/dotm-circular-4';
import { useAutoExpand } from './useAutoExpand';
import { WidgetCollapse } from './WidgetCollapse';
import styles from './ResearchWorkflowWidgetUI.module.css';

export type ResearchWorkflowMode = 'web-search' | 'deep-research';
export type ResearchWorkflowStatus = 'in_progress' | 'complete';
export type ResearchSectionStatus = 'pending' | 'running' | 'complete' | 'failed';

export interface ResearchResultItem {
  id: string;
  title: string;
  domain?: string;
}

export interface ResearchSectionItem {
  id: string;
  query: string;
  resultCount?: number;
  status: ResearchSectionStatus;
  results?: ResearchResultItem[];
}

export interface ResearchWorkflowWidgetUIProps {
  mode: ResearchWorkflowMode;
  status: ResearchWorkflowStatus;
  title?: string;
  headerItems?: Array<string | { label: string; status?: 'complete' | 'running' | 'failed'; domain?: string; href?: string; faviconDomain?: string }>;
  sections: ResearchSectionItem[];
  initiallyExpandedIds?: string[];
  defaultExpanded?: boolean;
  /** Appear collapsed, then auto-expand the results after a beat (Guri's context-demo entrance). */
  autoExpand?: boolean;
}

const MODE_LABELS: Record<ResearchWorkflowMode, string> = {
  'web-search': 'Searched the web',
  'deep-research': 'Deep research in progress',
};

const STATUS_LABELS: Record<ResearchWorkflowStatus, string> = {
  in_progress: '',
  complete: '',
};

function getFaviconUrl(domain?: string) {
  if (!domain) return null;
  const normalizedDomain = domain.replace(/^https?:\/\//, '');
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(normalizedDomain)}&sz=64`;
}

function DoneOutlineCheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" />
      <path d="M5.25 8.2L7.05 10L10.75 6.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ResearchWorkflowWidgetUI({
  mode,
  status,
  title,
  headerItems = [],
  sections,
  defaultExpanded = true,
  autoExpand = false,
}: ResearchWorkflowWidgetUIProps) {
  const autoExpanded = useAutoExpand(autoExpand);
  const [manualExpanded, setManualExpanded] = useState<boolean | null>(null);
  const isExpanded = manualExpanded ?? (autoExpand ? autoExpanded : defaultExpanded);
  const hasHeaderItems = headerItems.length > 0;
  const hasHeaderTitle = (title ?? MODE_LABELS[mode]).trim().length > 0;
  const showMainHeader = !hasHeaderItems || hasHeaderTitle;
  const hasBodyContent = hasHeaderItems || sections.length > 0;
  const isInProgress = status === 'in_progress';

  return (
    <div className={styles.card} data-status={status}>
      {showMainHeader && (
        <button
          type="button"
          className={styles.header}
          onClick={() => hasBodyContent && setManualExpanded(!isExpanded)}
          data-expanded={isExpanded ? 'true' : 'false'}
          data-clickable={hasBodyContent ? 'true' : 'false'}
          data-status={status}
        >
          <div className={styles.headerLeft} data-header-only="true">
            <span className={styles.headerMatrix} data-lead-icon>
              {/* circular-4 spins while searching; complete settles to a plain CHECK. A dotmatrix held
                  static read as an animation frozen mid-beat ("weird… freezing" — Guri); a check is the
                  unambiguous done state, matching the row semantics ("Searched the web ✓"). */}
              {isInProgress ? (
                <DotmCircular4 size={16} dotSize={2} ariaLabel="Searching" />
              ) : (
                <DeckIcon name="check" size={16} aria-label="Searched" />
              )}
            </span>
            <span className={styles.headerLabel} data-shimmer={status === 'in_progress' ? 'true' : undefined}>
              {title ?? MODE_LABELS[mode]}
            </span>
            {hasBodyContent && (
              <DeckIcon name={isExpanded ? 'arrow-up-s' : 'arrow-down-s'} size={16} className={styles.headerChevron} />
            )}
          </div>
          {STATUS_LABELS[status] ? <span className={styles.headerStatus}>{STATUS_LABELS[status]}</span> : null}
        </button>
      )}

      <WidgetCollapse open={isExpanded && hasBodyContent} className={styles.content}>
        {hasHeaderItems && (
          <div className={styles.headerItems}>
            {headerItems.map((item, index) => {
              const isLast = index === headerItems.length - 1 && sections.length === 0 && status !== 'complete';
              const itemLabel = typeof item === 'string' ? item : item.label;
              const itemStatus = typeof item === 'string' ? 'running' : (item.status ?? 'running');
              const itemDomain = typeof item === 'string' ? undefined : item.domain;
              const itemHref = typeof item === 'string' ? undefined : item.href;
              const itemFaviconDomain = typeof item === 'string' ? undefined : item.faviconDomain;
              const resolvedHref = itemHref ?? (itemDomain ? `https://${itemDomain}` : undefined);
              return (
                <div
                  key={`${itemLabel}-${index}`}
                  className={styles.headerItem}
                  data-last={isLast ? 'true' : 'false'}
                  data-status={itemStatus}
                >
                  {itemFaviconDomain ? (
                    <img
                      src={getFaviconUrl(itemFaviconDomain) ?? undefined}
                      alt=""
                      aria-hidden
                      className={styles.headerItemFavicon}
                    />
                  ) : (
                    <DeckIcon name="global" size={16} className={styles.queryIcon} />
                  )}
                  <span
                    className={styles.headerItemLabel}
                    title={itemLabel}
                    data-shimmer={status === 'in_progress' && itemStatus === 'running' ? 'true' : undefined}
                  >
                    {itemLabel}
                  </span>
                  {itemDomain && resolvedHref ? (
                    <a
                      href={resolvedHref}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.headerItemDomainLink}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <span className={styles.headerItemDomain}>{itemDomain}</span>
                    </a>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {sections.map((section, index) => {
          const isLast = index === sections.length - 1 && status !== 'complete';
          const countLabel = typeof section.resultCount === 'number' ? `${section.resultCount} results` : null;

          return (
            <div
              key={section.id}
              className={styles.section}
              data-status={section.status}
              data-last={isLast ? 'true' : 'false'}
            >
              <div className={styles.sectionTrigger}>
                <DeckIcon name="global" size={16} className={styles.queryIcon} />
                <span className={styles.queryText}>{section.query}</span>
                <span className={styles.meta}>
                  {countLabel && <span className={styles.resultCount}>{countLabel}</span>}
                </span>
              </div>

              {section.results?.length ? (
                <div className={styles.resultsPanel}>
                  {section.results.map((result) => (
                    <div key={result.id} className={styles.resultRow}>
                      <span className={styles.resultTitleWrap}>
                        {getFaviconUrl(result.domain) ? (
                          <img
                            src={getFaviconUrl(result.domain) ?? undefined}
                            alt=""
                            aria-hidden
                            className={styles.resultFavicon}
                          />
                        ) : (
                          <DeckIcon name="global" size={16} className={styles.resultFallbackIcon} />
                        )}
                        <span className={styles.resultTitle}>{result.title}</span>
                      </span>
                      {result.domain ? (
                        <a
                          href={`https://${result.domain.replace(/^https?:\/\//, '')}`}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.resultDomainLink}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <span className={styles.resultDomain}>{result.domain}</span>
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}

        {status === 'complete' && (
          <div className={styles.section} data-last="true" data-status="complete">
            <div className={styles.sectionTrigger}>
              <span className={styles.doneIcon}>
                <DoneOutlineCheckIcon />
              </span>
              <span className={styles.queryText}>Done</span>
            </div>
          </div>
        )}
      </WidgetCollapse>
    </div>
  );
}

